// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 20-agent-plugins.md#marketplace-repo-bootstrap

import * as React from 'react';
import { RefreshCw, Trash2 } from 'lucide-react';

import { DataList } from '@/components/greenlight/DataList';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { relativeTime } from '@/components/greenlight/relativeTime';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { PluginMarketplaceStatus } from '@greenlight/shared';

interface MarketplaceRepositoryTableProps {
  repositories: PluginMarketplaceStatus[];
  pendingKey: string | null;
  onReconcile: (repository: PluginMarketplaceStatus) => Promise<unknown>;
  /** Omitted where unregistering isn't offered (the install wizard step); hides the row action. */
  onUnregister?: (repository: PluginMarketplaceStatus) => void;
}

function LastSync({ repository }: { repository: PluginMarketplaceStatus }): React.JSX.Element {
  return (
    <div className="flex min-w-40 flex-col gap-0.5">
      <span className="text-xs font-medium">{repository.last_synced_template_version}</span>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <MonoValue className="max-w-24 truncate" title={repository.last_synced_sha}>
          {repository.last_synced_sha.slice(0, 10)}
        </MonoValue>
        <span aria-hidden="true">·</span>
        <time
          dateTime={repository.last_synced_at}
          title={new Date(repository.last_synced_at).toLocaleString()}
        >
          {relativeTime(repository.last_synced_at)}
        </time>
      </div>
    </div>
  );
}

export function MarketplaceRepositoryTable({
  repositories,
  pendingKey,
  onReconcile,
  onUnregister,
}: MarketplaceRepositoryTableProps): React.JSX.Element {
  return (
    <div data-testid="marketplace-repositories">
      <DataList
        items={repositories}
        ariaLabel="Marketplace repositories"
        rowKey={(repository) => repository.repo_full_name}
        columns={[
          {
            key: 'repo_full_name',
            header: 'Repository',
            primary: true,
            className: 'min-w-56 font-medium',
            render: (repository) => <MonoValue copyable>{repository.repo_full_name}</MonoValue>,
          },
          {
            key: 'visibility',
            header: 'Visibility',
            render: (repository) => (
              <Badge variant="outline" className="font-normal">
                {repository.visibility === 'public' ? 'Public' : 'Private'}
              </Badge>
            ),
          },
          {
            key: 'default_branch',
            header: 'Branch',
            priority: 'md',
            render: (repository) => (
              <span className="font-mono text-xs text-muted-foreground">
                {repository.default_branch}
              </span>
            ),
          },
          {
            key: 'last_sync',
            header: 'Last sync',
            priority: 'lg',
            render: (repository) => <LastSync repository={repository} />,
          },
        ]}
        rowAction={(repository) => {
          const pending = pendingKey === repository.repo_full_name;
          return (
            <TooltipProvider>
              <div className="flex items-center gap-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-11 md:h-8"
                      disabled={pendingKey !== null}
                      onClick={() => void onReconcile(repository)}
                      aria-label="Check repo"
                    >
                      <RefreshCw
                        className={cn('h-4 w-4', pending && 'animate-spin')}
                        aria-hidden="true"
                      />
                      <span className="hidden xl:inline">
                        {pending ? 'Checking…' : 'Check repo'}
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Re-runs create for this repository — recreates it if it was deleted on GitHub.
                  </TooltipContent>
                </Tooltip>
                {onUnregister && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11 md:h-8 md:w-8"
                        disabled={pendingKey !== null}
                        onClick={() => onUnregister(repository)}
                        aria-label={`Unregister ${repository.repo_full_name}`}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Unregister — the repository stays on GitHub untouched, agents stop getting
                      updates.
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </TooltipProvider>
          );
        }}
      />
    </div>
  );
}
